import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../../lib/api';
import { PageHeader } from '../../pages/AppShell';
import { Banner, PageLoading } from '../../components/ui';
import Icon from '../../components/Icon';
import MapView, { hasTiles } from './MapView';
import type { TrackingDay, TrackedDay, TimelineEntry, Stop } from './types';

interface ReportRow { userId: string; fullName: string; workDate: string; }

const fmtDur = (min: number) => (min < 60 ? `${min}m` : `${Math.floor(min / 60)}h ${min % 60}m`);
const timeOf = (iso: string | null) => (iso
  ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');
const minsBetween = (a: string, b: string | null) => (b
  ? Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000)) : 0);
const dayLabel = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const inr = (n: number) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);
const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

export default function RouteReplayPage() {
  const navigate = useNavigate();
  const initialUser = (useLocation().state as { userId?: string } | null)?.userId ?? null;

  const [reps, setReps] = useState<{ userId: string; fullName: string }[]>([]);
  const [userId, setUserId] = useState<string | null>(initialUser);
  const [days, setDays] = useState<TrackedDay[]>([]);
  const [date, setDate] = useState<string | null>(null);
  const [day, setDay] = useState<TrackingDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reps with any tracking data in the last two weeks — the picker.
  useEffect(() => {
    (async () => {
      try {
        const to = new Date();
        const from = new Date(Date.now() - 13 * 864e5);
        const iso = (d: Date) => d.toISOString().slice(0, 10);
        const rows = await api.get<ReportRow[]>(`/tracking/report?from=${iso(from)}&to=${iso(to)}`);
        const uniq = new Map<string, string>();
        rows.forEach((r) => uniq.set(r.userId, r.fullName));
        const list = [...uniq].map(([id, name]) => ({ userId: id, fullName: name }));
        setReps(list);
        setUserId((cur) => cur ?? list[0]?.userId ?? null);
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load tracked reps.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Days for the chosen rep (newest first, as the API returns them).
  useEffect(() => {
    if (!userId) return;
    (async () => {
      const list = await api.get<TrackedDay[]>(`/tracking/users/${userId}/days`);
      setDays(list);
      setDate(list[0]?.workDate ?? null);
    })().catch(() => setDays([]));
  }, [userId]);

  // The day itself.
  useEffect(() => {
    if (!userId || !date) { setDay(null); return; }
    setDay(null);
    (async () => {
      setDay(await api.get<TrackingDay>(`/tracking/users/${userId}/days/${date}`));
    })().catch((err) => setError(err instanceof ApiClientError ? err.message : 'Could not load that day.'));
  }, [userId, date]);

  if (loading) return (<><PageHeader title="Route replay" /><div className="page"><PageLoading rows={3} /></div></>);
  if (error && reps.length === 0) return (<><PageHeader title="Route replay" /><div className="page"><Banner kind="danger">{error}</Banner></div></>);
  if (reps.length === 0) {
    return (
      <>
        <PageHeader title="Route replay" subtitle="Play back a rep's day on the map." />
        <div className="page"><Banner kind="info">No tracked routes yet. Routes appear here once reps check in with tracking on.</Banner></div>
      </>
    );
  }

  const rep = reps.find((r) => r.userId === userId) ?? null;

  return (
    <>
      <PageHeader
        title="Route replay"
        actions={(
          <>
            <select className="input tk-select" value={userId ?? ''} onChange={(e) => setUserId(e.target.value)}>
              {reps.map((r) => <option key={r.userId} value={r.userId}>{r.fullName}</option>)}
            </select>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/tracking')}>Live map</button>
          </>
        )}
      />
      <div className="page rr-page">
        <RepBar repName={rep?.fullName ?? ''} day={day} days={days} date={date} onDate={setDate} />
        {day ? <Replay day={day} /> : <PageLoading rows={2} />}
      </div>
    </>
  );
}

/**
 * Who and when: the rep, what their phone is running (and any setup issue
 * that explains a quiet signal), and ‹ › through their tracked days.
 */
function RepBar({ repName, day, days, date, onDate }: {
  repName: string; day: TrackingDay | null; days: TrackedDay[];
  date: string | null; onDate: (d: string) => void;
}) {
  const idx = days.findIndex((d) => d.workDate === date);
  // Days arrive newest-first, so "previous day" is the next index.
  const older = idx >= 0 ? days[idx + 1] : undefined;
  const newer = idx > 0 ? days[idx - 1] : undefined;

  const device = day ? [...day.sessions].reverse().find((s) => s.device)?.device ?? null : null;
  const perms = day?.permissions;
  const setupIssue = !perms ? null
    : perms.bgLocationGranted === false ? 'Location not set to “Always” — tracking stops when the screen locks'
      : perms.batteryExempt === false ? 'Battery optimisation is on — the phone may kill tracking'
        : null;
  const bits: string[] = [];
  if (device?.app) bits.push(`App v${device.app}`);
  if (device?.model) bits.push(device.model);
  else if (device?.os) bits.push(device.os);

  return (
    <div className="rr-cbar">
      <span className="rr-av">{initialsOf(repName)}</span>
      <div className="rr-meta">
        <div className="rr-nm">{repName || '—'}</div>
        <div className="rr-ro">
          {bits.length ? bits.join(' · ') : 'Tracked day'}
          {setupIssue ? <span className="rr-issue"><Icon name="alert" size={12} /> {setupIssue}</span> : null}
        </div>
      </div>
      <div className="rr-daynav">
        <button type="button" disabled={!older} onClick={() => older && onDate(older.workDate)} aria-label="Previous day">‹</button>
        <b>{date ? dayLabel(date) : '—'}</b>
        <button type="button" disabled={!newer} onClick={() => newer && onDate(newer.workDate)} aria-label="Next day">›</button>
      </div>
    </div>
  );
}

function Replay({ day }: { day: TrackingDay }) {
  const [frac, setFrac] = useState(1);
  const [highlight, setHighlight] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<1 | 2 | 4>(1);
  const raf = useRef<number | null>(null);

  const stop = useCallback(() => { setPlaying(false); if (raf.current) cancelAnimationFrame(raf.current); }, []);
  const play = useCallback(() => {
    setPlaying(true);
    const from = frac >= 1 ? 0 : frac;
    const t0 = performance.now();
    const dur = (12000 / speed) * (1 - from);
    const step = (now: number) => {
      const f = Math.min(1, from + (now - t0) / dur * (1 - from));
      setFrac(f);
      if (f >= 1) { setPlaying(false); return; }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, [frac, speed]);
  useEffect(() => () => { if (raf.current) cancelAnimationFrame(raf.current); }, []);
  // Reset the scrubber when a different day loads.
  useEffect(() => { stop(); setFrac(1); setHighlight(null); }, [day.workDate, day.userId, stop]);

  // The clock at the current scrub position.
  const clock = useMemo(() => {
    if (day.path.length === 0) return '—';
    const idx = Math.min(day.path.length - 1, Math.max(0, Math.round(day.path.length * frac) - 1));
    return timeOf(day.path[idx]?.t ?? null);
  }, [day.path, frac]);

  const s = day.summary;
  const hasRoute = day.rawPoints > 0;

  // A dwell in the timeline links to its numbered marker by stop id.
  const stopIndex = useMemo(() => new Map(day.stops.map((st, i) => [st.id, i])), [day.stops]);

  // Where each stop sits on the scrubber (ticks), as a fraction of the path.
  const ticks = useMemo(() => {
    if (day.path.length < 2) return [] as number[];
    const n = day.path.length - 1;
    return day.stops.map((st) => {
      const t = new Date(st.startedAt).getTime();
      let i = day.path.findIndex((p) => new Date(p.t).getTime() >= t);
      if (i < 0) i = n;
      return i / n;
    });
  }, [day.path, day.stops]);

  // On duty = the span of the day's tracking sessions.
  const onDutyMin = useMemo(() => {
    const total = day.sessions.reduce((acc, se) => {
      const end = se.endedAt ?? se.lastSeenAt;
      return acc + (end ? minsBetween(se.startedAt, end) : 0);
    }, 0);
    return total || (s.movingMinutes + s.stationaryMinutes);
  }, [day.sessions, s.movingMinutes, s.stationaryMinutes]);

  const gapCount = day.timeline.filter((e) => e.type === 'event' && e.kind === 'gap').length;
  const flags = s.unloggedStops;
  const orders = s.orders ?? 0;

  const cycleSpeed = () => setSpeed((v) => (v === 1 ? 2 : v === 2 ? 4 : 1));

  return (
    <>
      <div className="rr-summary">
        <div className="rr-s"><b className="tnum">{s.distanceKm}</b><span>km travelled</span></div>
        <div className="rr-s"><b className="tnum">{s.stops}</b><span>stops</span></div>
        <div className="rr-s"><b className="tnum">{orders}</b><span>{orders === 1 ? 'order' : 'orders'}{s.orderTotal ? ` · ₹${inr(s.orderTotal)}` : ''}</span></div>
        <div className="rr-s"><b className="tnum">{fmtDur(onDutyMin)}</b><span>on duty</span></div>
        <div className={`rr-s${gapCount || flags ? ' flag' : ''}`}>
          <b className="tnum">{gapCount}</b>
          <span>{gapCount === 1 ? 'GPS gap' : 'GPS gaps'} · {flags} {flags === 1 ? 'flag' : 'flags'}</span>
        </div>
      </div>

      <div className="rr-replay">
        <div className="rr-card">
          <div className="rr-mapbox">
            {hasRoute ? (
              <MapView
                mode="route"
                path={day.path}
                stops={day.stops}
                events={day.events}
                customers={day.customers}
                scrubFrac={frac}
                highlightStop={highlight}
                onStopClick={(i) => setHighlight(i === highlight ? null : i)}
              />
            ) : <div className="tk-empty-map">No route recorded for this day.</div>}
            {!hasTiles && hasRoute && (
              <div className="tk-nomap">Route shape only — add a map key for streets.</div>
            )}
            {hasRoute && (
              <div className="rr-legend">
                <span><i className="rr-ld" style={{ background: 'var(--success)' }} />Order booked</span>
                <span><i className="rr-ld" style={{ background: 'var(--ink-faint)' }} />No order</span>
                <span><i className="rr-ld" style={{ background: 'var(--danger)' }} />Flagged stop</span>
                <span><i className="rr-ld sq" style={{ background: 'var(--danger)' }} />GPS gap</span>
              </div>
            )}
          </div>
          {hasRoute && (
            <div className="rr-controls">
              <button type="button" className="rr-play" onClick={playing ? stop : play} aria-label={playing ? 'Pause' : 'Play'}>
                {playing ? '❚❚' : '▶'}
              </button>
              <span className="rr-clock tnum">{clock}</span>
              <div className="rr-scrub">
                <input
                  type="range" min={0} max={1000} value={Math.round(frac * 1000)}
                  aria-label="Scrub route"
                  onChange={(e) => { stop(); setFrac(Number(e.target.value) / 1000); }}
                />
                <div className="rr-ticks" aria-hidden="true">
                  {ticks.map((f, i) => <i key={i} style={{ left: `${(f * 100).toFixed(2)}%` }} />)}
                </div>
              </div>
              <button type="button" className="rr-spd" onClick={cycleSpeed} aria-label="Playback speed">{speed}×</button>
            </div>
          )}
        </div>

        <aside className="rr-card rr-panel">
          <h3>Day timeline</h3>
          {day.timeline.length === 0 ? (
            <p className="faint small">Nothing to plot yet.</p>
          ) : day.timeline.map((entry, i) => {
            if (entry.type === 'travel') return null;
            if (entry.type === 'event') {
              // eslint-disable-next-line react/no-array-index-key
              return <EventChip key={i} entry={entry} />;
            }
            const idx = stopIndex.get(entry.stopId) ?? null;
            const st = idx != null ? day.stops[idx] ?? null : null;
            return (
              <StopRow
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                n={idx != null ? idx + 1 : null}
                entry={entry}
                stop={st}
                active={idx != null && idx === highlight}
                onSelect={idx != null ? () => setHighlight(idx === highlight ? null : idx) : undefined}
              />
            );
          })}
        </aside>
      </div>
    </>
  );
}

type Dwell = Extract<TimelineEntry, { type: 'visit' | 'unscheduled_stop' }>;

/**
 * One numbered row of the day: the badge matches the marker on the map,
 * and the chip on the right says what the stop produced — an order, no
 * order, or a flag (stopped at a shop, nothing logged).
 */
function StopRow({ n, entry, stop, active, onSelect }: {
  n: number | null; entry: Dwell; stop: Stop | null; active: boolean; onSelect?: () => void;
}) {
  const mins = minsBetween(entry.startedAt, entry.endedAt);
  const time = `${timeOf(entry.startedAt)}–${timeOf(entry.endedAt)} · ${fmtDur(mins)}${entry.isOpen ? ' · still here' : ''}`;

  let col = 'var(--ink-faint)';
  let name = '';
  let chip: { kind: 'ok' | 'no' | 'warn'; text: string } = { kind: 'no', text: 'No order' };

  if (entry.type === 'visit') {
    name = entry.name;
    if (entry.order && entry.order.count > 0) {
      col = 'var(--success)';
      chip = { kind: 'ok', text: `Order ₹${inr(entry.order.total)}` };
    }
  } else if (entry.nearCustomer) {
    col = 'var(--danger)';
    name = entry.nearCustomer;
    const dist = stop?.customer?.distanceM;
    chip = { kind: 'warn', text: dist != null ? `Flagged · ${Math.round(dist)} m from pin` : 'Flagged · no visit logged' };
  } else {
    name = 'Unscheduled stop';
    chip = { kind: 'no', text: 'Unscheduled' };
  }

  const clickable = Boolean(onSelect);
  return (
    <div
      className={`rr-stop${active ? ' active' : ''}`}
      onClick={onSelect}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onSelect!(); } : undefined}
      style={clickable ? undefined : { cursor: 'default' }}
    >
      <span className="rr-num" style={{ background: col }}>{n ?? '·'}</span>
      <div className="rr-b">
        <div className="rr-stop-nm">{name}</div>
        <div className="rr-stop-tm">{time}</div>
        {entry.type === 'unscheduled_stop' ? <StopAddress stopId={entry.stopId} /> : null}
        {entry.type === 'unscheduled_stop' && entry.nearCustomer
          ? <div className="rr-stop-warn">⚠ Near {entry.nearCustomer} — no visit logged</div> : null}
      </div>
      <span className={`rr-tag ${chip.kind}`}>{chip.text}</span>
    </div>
  );
}

const EVENT_META: Record<string, { name: string; danger: boolean }> = {
  gap: { name: 'GPS gap', danger: true },
  gps_off: { name: 'GPS turned off', danger: true },
  internet_lost: { name: 'Internet switched off', danger: true },
  low_battery: { name: 'Low battery', danger: false },
  mock_location: { name: 'Fake GPS detected', danger: true },
  permission_revoked: { name: 'Location permission removed', danger: true },
  app_killed: { name: 'Tracking stopped by phone', danger: false },
};

// The plain-language cause under a signal event, so a reviewer can tell at a
// glance whether the rep turned GPS off themselves or the signal just dropped.
const CAUSE_SUB: Record<string, string> = {
  manual: 'switched off on the phone',
  auto: 'possible signal loss',
};

/** A signal event as a chip between stops: ⚠ GPS gap 11:10–11:25 · 15 min · possible signal loss */
function EventChip({ entry }: { entry: Extract<TimelineEntry, { type: 'event' }> }) {
  const m = EVENT_META[entry.kind] ?? { name: entry.kind, danger: false };
  const mins = entry.endedAt ? minsBetween(entry.startedAt, entry.endedAt) : 0;
  const parts = [
    `${timeOf(entry.startedAt)}${entry.endedAt ? `–${timeOf(entry.endedAt)}` : ''}`,
    mins ? `${mins} min` : null,
    entry.cause ? CAUSE_SUB[entry.cause] ?? null : null,
  ].filter(Boolean);
  return (
    <div className={`rr-gapchip${m.danger ? '' : ' warn'}`}>
      ⚠ {m.name} {parts.join(' · ')}
    </div>
  );
}

/**
 * The address of an unscheduled stop, revealed on tap. Resolving a
 * coordinate is charged per call, so it is fetched only when asked and
 * cached on the server — a second viewer of the same stop pays nothing.
 * The click is stopped from bubbling so it does not also toggle the map
 * highlight on the row.
 */
function StopAddress({ stopId }: { stopId: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [address, setAddress] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reveal = async () => {
    setState('loading');
    try {
      const r = await api.get<{ address: string | null; configured?: boolean }>(`/tracking/stops/${stopId}/address`);
      setAddress(r.address);
      // No provider set up → tell the admin that, and let them try again
      // once it is (the server never caches an empty result).
      setNote(r.address ? null : (r.configured === false
        ? 'Address lookup isn’t set up yet'
        : 'No address found for this spot'));
    } catch {
      setAddress(null);
      setNote('Could not look up the address');
    } finally {
      setState('done');
    }
  };

  if (state === 'idle') {
    return (
      <button type="button" className="tk-addr-btn" onClick={(e) => { e.stopPropagation(); void reveal(); }}>
        Show address
      </button>
    );
  }
  if (state === 'loading') return <div className="tk-stop-sub faint">Resolving…</div>;
  if (address) return <div className="tk-stop-sub">{address}</div>;
  return (
    <button type="button" className="tk-addr-btn" onClick={(e) => { e.stopPropagation(); void reveal(); }}>
      {note ?? 'Location not resolved'} · Retry
    </button>
  );
}
